/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {GenericObject} from '@ion/core/src/types/entities';
import AccordionWrapper from '../../accordion/Accordion';
import {AccordionSectionHeader} from '../../accordion/current/AccordionSectionHeader';
import {getAccordionTopBorderStyle} from '../../assetdetails/current/assetDetailsAccordionStyles';
import {VisionHistoryList} from './VisionHistoryList';
import {VisionPlayer} from './VisionPlayer';
import {VisionShowMoreCta} from './VisionShowMoreCta';

export interface VisionAccordionProps {
  title: string;
  count?: number;
  infoTooltip?: string;
  featuredEvent?: GenericObject;
  videoSource?: string;
  onEventClick?: (event: GenericObject) => void;
  audio?: boolean;
  playbackLabel?: string;
  historyEvents?: GenericObject[];
  isVideoAvailable?: (event: GenericObject) => boolean;
  videoNotAvailableLabel?: string;
  onShowMoreClick?: () => void;
  showMoreLabel?: string;
  showShowMore?: boolean;
  // the next events are loading ...
  showMoreLoading?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // every Asset Details Drawer accordion gets this 2px teal top border to
  // separate it from the card above it ...
  header  : getAccordionTopBorderStyle(),
  details : {
    display       : 'flex',
    flexDirection : 'column'
  }
}));

/**
 * VisionAccordion composes the current theme's Vision section for the Asset
 * Details Drawer - `AccordionSectionHeader` for its title/count,
 * `VisionPlayer` for the featured event's preview, `VisionHistoryList` for
 * past camera events, and an optional `VisionShowMoreCta` pagination row -
 * inside the shared `AccordionWrapper`
 * (`@ion/dls/src/components/accordion/Accordion`). Data-fetching and
 * feature-flag gating stay owned by the container this replaces (the legacy
 * `VisionEventsAccordion`); this component is presentation only.
 *
 * Expansion is uncontrolled via `defaultExpanded` (expanded by default);
 * passing `expanded` and `onExpand` instead hands control to the caller,
 * which the Asset Details Drawer uses to drive its header's
 * expand/collapse-all control. Both are forwarded straight to
 * `AccordionWrapper`.
 *
 * ```typescript
 *
 *   import {VisionAccordion} from
 *     '@ion/dls/src/components/vision/current/VisionAccordion';
 *   ...
 *
 *   <VisionAccordion title='VISION'
 *     count={events.length}
 *     featuredEvent={featuredEvent}
 *     videoSource={videoSource}
 *     onEventClick={onIonVisionClick}
 *     audio
 *     historyEvents={subsequentEvents}
 *     isVideoAvailable={hasVideoAvailable}
 *     videoNotAvailableLabel='This Video Is No Longer Available'
 *     showShowMore={hasNextPage}
 *     showMoreLoading={isFetchingNextPage}
 *     showMoreLabel='Show More'
 *     onShowMoreClick={fetchNextPage}
 *     defaultExpanded />
 *
 * ```
 *
 */
export const VisionAccordion = ({
  title,
  count,
  infoTooltip,
  featuredEvent,
  videoSource,
  onEventClick,
  audio = false,
  playbackLabel,
  historyEvents,
  isVideoAvailable,
  videoNotAvailableLabel,
  onShowMoreClick,
  showMoreLabel,
  showShowMore = false,
  showMoreLoading = false,
  defaultExpanded = true,
  expanded,
  onExpand
}: VisionAccordionProps) => {
  const classes = useStyles();

  const onFeaturedEventClick = useCallback(function() {
    onEventClick?.(featuredEvent);
  }, [onEventClick, featuredEvent]);

  return (
    // no padding around the details - the player, history cards and show more
    // each carry their own (24px 16px for the player, per Figma), and the
    // accordion's own 8px 16px 24px around them doubled the spacing ...
    <AccordionWrapper isCustomStyle
      className={classes.header}
      padding='0px'
      header={
        <AccordionSectionHeader title={title}
          count={count}
          infoTooltip={infoTooltip} />
      }
      details={
        <div className={classes.details}>
          {featuredEvent && videoSource && (
            <VisionPlayer event={featuredEvent}
              videoSource={videoSource}
              eventClick={onFeaturedEventClick}
              audio={audio}
              playbackLabel={playbackLabel} />
          )}
          <VisionHistoryList events={historyEvents}
            eventClick={onEventClick}
            isVideoAvailable={isVideoAvailable}
            toolTipLabel={videoNotAvailableLabel} />
          {showShowMore && (
            <VisionShowMoreCta onClick={onShowMoreClick}
              label={showMoreLabel}
              isLoading={showMoreLoading} />
          )}
        </div>
      }
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onExpand={onExpand} />
  );
};

export default React.memo(VisionAccordion);
